import React from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center pt-20 pb-16 px-6 hero-gradient relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="text-center lg:text-left">
            {/* Certification Badge */}
            <div className="flex justify-center lg:justify-start mb-6 animate-fade-in" data-testid="hero-certification-badge">
              <div className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-lg border border-gray-100">
                <img
                  src="/google-it-certificate.png"
                  alt="Google IT Support Certificate"
                  className="w-14 h-14 object-contain flex-shrink-0"
                />
                <div className="text-left">
                  <p className="text-gray-900 font-bold text-sm leading-tight">Google IT Support Certificate</p>
                  <p className="text-gray-500 text-xs">Industry-recognized credential</p>
                </div>
              </div>
            </div>

            {/* Badge */}
            <div className="flex justify-center lg:justify-start mb-8 animate-fade-in">
              <div className="badge">
                <Sparkles size={16} />
                <span>NON-PROFIT TECH EDUCATION</span>
                <Sparkles size={16} />
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 animate-fade-in-up">
              Learn IT & AI.
            </h1>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-200">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
                Earn While Learning.
              </span>
            </h2>
            
            {/* Tagline */}
            <p className="text-2xl md:text-3xl font-semibold italic text-gray-700 mb-8 animate-fade-in-up animation-delay-300">
              "Bridging You to the Future You Deserve.."
            </p>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-700 mb-10 leading-relaxed animate-fade-in-up animation-delay-400">
              Break into tech with zero financial barriers. Start earning from day one while mastering in-demand skills.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center mb-10 animate-fade-in-up animation-delay-600">
              <button onClick={scrollToContact} className="btn-primary group">
                <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
                Start Your Journey
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={scrollToAbout} className="btn-secondary">
                Learn More
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 animate-fade-in-up animation-delay-800">
              <div className="stat-card hover-lift">
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-2">
                  95%
                </div>
                <div className="text-gray-600 font-medium text-sm">Job Placement</div>
              </div>
              <div className="stat-card hover-lift">
                <div className="text-4xl font-bold bg-gradient-to-r from-green-500 to-teal-600 bg-clip-text text-transparent mb-2">
                  $55k
                </div>
                <div className="text-gray-600 font-medium text-sm">Avg. Salary</div>
              </div>
            </div>
          </div>

          {/* Right Side - Happy Students Images */}
          <div className="hidden lg:block animate-fade-in-up animation-delay-400">
            <div className="relative">
              {/* Main Featured Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1758270705140-4e39219d4bba?w=600&q=80"
                  alt="Happy diverse students celebrating together"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                    <p className="text-gray-900 font-bold text-lg">Join 317+ Happy Students!</p>
                    <p className="text-gray-600 text-sm">Start your tech journey today</p>
                  </div>
                </div>
              </div>

              {/* Secondary Images Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1594077841990-3909f3a482a9?w=300&q=80"
                    alt="Student with technology"
                    className="w-full h-32 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
                  <img 
                    src="https://images.unsplash.com/photo-1758521540986-d9333a5c9685?w=300&q=80"
                    alt="Excited student"
                    className="w-full h-32 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300">
                  <img 
                    src="https://images.pexels.com/photos/1438072/pexels-photo-1438072.jpeg?w=300&q=80"
                    alt="Students collaborating"
                    className="w-full h-32 object-cover"
                  />
                </div>
              </div>

              {/* Floating Success Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-cyan-400 to-blue-500 text-white rounded-2xl p-4 shadow-2xl animate-bounce-slow">
                <div className="text-center">
                  <div className="text-2xl font-bold">100%</div>
                  <div className="text-xs font-medium">Free</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;